import { createBlockage } from "@/hooks/api/blockage";
import { SESSION_APPOINTMENT_TYPES } from "@/hooks/api/config";
import apiCoreAdapter from "@/hooks/apiCoreAdapter";
import { PsychologistFreeScheduleStatus } from "@/components/SessionDebug";
import type { ComunidadCalendarEvent } from "@/hooks/api/comunidad";
import { formacionBlockReason, pad, rangesOverlap, splitWindowIntoBlockages } from "./formacionBlockWindow";

/**
 * Bloqueo automático de agenda al anotarse a una formación de Comunidad.
 *
 * Es el mismo bloqueo de "día específico" que hace la Agenda (`createBlockage`, el que usa
 * `BlockSchedulePanel`): quita la disponibilidad de ese tramo. La diferencia es que acá el
 * día y la hora ya vienen del evento (`startsAt` en UTC + `durationMinutes`), y `createBlockage`
 * recibe fecha y horas en UTC — por eso no hace falta convertir por huso horario.
 * La lógica pura (partir la ventana, solapamiento) vive en `formacionBlockWindow.ts`.
 */

/** El bloqueo trabaja en tramos de 30 min. */
const HALF_HOUR_MS = 30 * 60_000;

/** Duración por defecto de una sesión cuando no se puede resolver por tipo de proceso. */
const DEFAULT_SESSION_MINUTES = 60;

/** Sesiones que ocupan la agenda: cualquier tipo, todos los estados menos `cancelled`. */
const OCCUPYING_SESSION_STATUSES = "active,incomplete,settled,free,lost";

export type FormacionBlockResult =
  | { status: "blocked" }
  | { status: "already_blocked" }
  /** El evento ya empezó o no tiene fecha: no hay nada que bloquear. */
  | { status: "skipped" }
  /** Hay una sesión agendada que pisa el horario: no se puede anotar. */
  | { status: "conflict"; sessionStartIso: string }
  /** No se pudo verificar o bloquear: el flujo sigue, pero la agenda no quedó bloqueada. */
  | { status: "error" };

function sessionDurationMinutes(typeName?: string): number {
  const apt = SESSION_APPOINTMENT_TYPES.find((a) => a.name === typeName);
  return apt?.duration || DEFAULT_SESSION_MINUTES;
}

export interface OccupyingSession {
  /** Inicio de la sesión (ISO / instante). */
  startIso: string;
  /** Nombre del paciente (o pacientes) de la sesión; vacío si no viene. */
  patientName: string;
}

/**
 * Sesiones agendadas (todas menos las canceladas) que se pisan con la ventana [startMs, endMs),
 * ordenadas por horario. La usan "Anotarme" en una formación y "Bloquear mi agenda" en el aviso
 * de feriado. Lanza si no se pudo consultar. Solo lee: no toca ninguna sesión.
 */
export async function findOccupyingSessions(startMs: number, endMs: number): Promise<OccupyingSession[]> {
  // Rango amplio (un día de margen a cada lado): la consulta se hace por fecha, y la
  // comparación real se hace después por instante.
  const pickDay = (ms: number, offsetDays: number, time: string) => {
    const d = new Date(ms + offsetDays * 86_400_000);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${time}`;
  };
  const resp = await apiCoreAdapter.psychologistSessionsList({
    from: pickDay(startMs, -1, "00:00:00"),
    to: pickDay(endMs, 1, "23:59:59"),
    take: 200,
    status: OCCUPYING_SESSION_STATUSES,
  });
  const found: OccupyingSession[] = [];
  for (const s of resp.data) {
    if (s.status === "cancelled") continue;
    const sStart = new Date(s.date).getTime();
    if (Number.isNaN(sStart)) continue;
    const sEnd = sStart + sessionDurationMinutes(s.process_info?.type_name) * 60_000;
    if (!rangesOverlap(startMs, endMs, sStart, sEnd)) continue;
    found.push({
      startIso: new Date(sStart).toISOString(),
      patientName: (s.patients ?? []).map((p) => p.name).filter(Boolean).join(", "),
    });
  }
  return found.sort((a, b) => a.startIso.localeCompare(b.startIso));
}

/** Busca una sesión agendada que se pise con la ventana. Lanza si no se pudo consultar. */
async function findConflictingSession(startMs: number, endMs: number): Promise<string | null> {
  const [first] = await findOccupyingSessions(startMs, endMs);
  return first ? first.startIso : null;
}

/**
 * ¿Ya hay un bloqueo nuestro que cubre TODA la franja? (evita duplicarlo si toca "Anotarme"
 * otra vez). Tiene que estar cubierto cada tramo de 30 min: un bloqueo viejo que solo roza la
 * franja (ej. 10:30–11:30 contra 11:00–12:00) no cuenta, o quedaría un tramo sin bloquear.
 */
async function alreadyBlocked(startMs: number, endMs: number, reason: string): Promise<boolean> {
  try {
    const slots = await apiCoreAdapter.psychologistAvailability({
      from: new Date(startMs).toISOString(),
      to: new Date(endMs).toISOString(),
      source: "psi_platform",
    });
    const covered = new Set(
      slots
        .filter((s) => s.status !== PsychologistFreeScheduleStatus.AVAILABLE && s.reason === reason)
        .map((s) => new Date(s.slot).getTime()),
    );
    for (let t = startMs; t < endMs; t += HALF_HOUR_MS) {
      if (!covered.has(t)) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Antes de abrir Google Calendar: verifica que no haya una sesión agendada en el horario de
 * la formación y, si no la hay, bloquea la agenda en ese tramo. Nunca lanza.
 *
 * A diferencia del panel "Bloquear" de la Agenda, NO exige anticipación mínima (allá son 48 h):
 * una formación se puede bloquear aunque falten pocas horas. La única condición es que no haya
 * ninguna sesión agendada en ese horario, de cualquier tipo. Solo se omite si el evento ya empezó.
 */
export async function blockAgendaForFormacion(ev: ComunidadCalendarEvent): Promise<FormacionBlockResult> {
  if (!ev.startsAt) return { status: "skipped" };
  const startMs = new Date(ev.startsAt).getTime();
  const endMs = startMs + ev.durationMinutes * 60_000;
  if (Number.isNaN(startMs) || endMs <= Date.now()) return { status: "skipped" };

  try {
    const conflict = await findConflictingSession(startMs, endMs);
    if (conflict) return { status: "conflict", sessionStartIso: conflict };

    const reason = formacionBlockReason(ev);
    if (await alreadyBlocked(startMs, endMs, reason)) {
      return { status: "already_blocked" };
    }
    for (const chunk of splitWindowIntoBlockages(ev.startsAt, ev.durationMinutes)) {
      await createBlockage({ ...chunk, reason });
    }
    return { status: "blocked" };
  } catch {
    return { status: "error" };
  }
}
